import React, { useState, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  Chip,
  Alert,
  Collapse,
  IconButton,
  LinearProgress,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  useMediaQuery,
  useTheme,
  Button,
} from '@mui/material';
import {
  Shield,
  CheckCircle,
  Warning,
  Error as ErrorIcon,
  ExpandMore,
  ExpandLess,
  Security,
  Speed,
  Email,
  Schedule,
  TrendingUp,
  Dns,
  VerifiedUser,
  Lightbulb,
  Assessment,
  ContentCopy,
} from '@mui/icons-material';

/**
 * EmailDeliverabilityGuide - Professional Email Deliverability Dashboard
 * Provides real-time deliverability scoring and actionable guidance
 * Specific to trucking/logistics outreach campaigns
 */

// Best send times for B2B trucking industry (Eastern Time)
const BEST_SEND_TIMES = [
  { day: 'Tuesday', time: '8:00 AM - 10:00 AM ET', score: 95 },
  { day: 'Wednesday', time: '9:00 AM - 11:00 AM ET', score: 92 },
  { day: 'Thursday', time: '8:00 AM - 10:00 AM ET', score: 90 },
  { day: 'Monday', time: '10:00 AM - 12:00 PM ET', score: 85 },
  { day: 'Friday', time: '8:00 AM - 10:00 AM ET', score: 78 },
];

// Trucking-specific subject line templates that avoid spam
const SUBJECT_TEMPLATES = [
  { template: 'Partnership Opportunity for {{company}}', category: 'Partnership' },
  { template: 'Freight Capacity Available - {{state}} Lanes', category: 'Capacity' },
  { template: 'Load Opportunities for DOT# {{dot}}', category: 'Loads' },
  { template: 'Quick Question About Your Fleet Operations', category: 'Engagement' },
  { template: '{{company}} - New Lane Opportunities Available', category: 'Outreach' },
  { template: 'Connecting With Carriers in {{state}}', category: 'Regional' },
  { template: 'Following Up - Freight Partnership Discussion', category: 'Follow-up' },
  { template: 'Dedicated Lanes for {{company}} Fleet', category: 'Dedicated' },
];

const EmailDeliverabilityGuide = ({
  accounts = [],
  compose = {},
  spamScore = 0,
  accountUsageToday = {},
  campaignPlan = {},
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [expanded, setExpanded] = useState(false);
  const [activeSection, setActiveSection] = useState('overview');
  const [copiedTemplate, setCopiedTemplate] = useState(null);

  // Calculate comprehensive deliverability score
  const deliverabilityReport = useMemo(() => {
    let score = 100;
    const issues = [];
    const tips = [];

    // 1. Account Health Check
    const activeAccounts = accounts.filter(a => a.status !== 'failed' && a.status !== 'disabled');
    if (activeAccounts.length === 0) {
      score -= 40;
      issues.push({ severity: 'critical', text: 'No active email accounts configured', fix: 'Add at least one verified SMTP account' });
    } else if (activeAccounts.length < 3) {
      score -= 10;
      tips.push('Add more email accounts (3-5 recommended) for better rotation and deliverability');
    }

    // 2. Daily Volume Check
    const totalUsed = Object.values(accountUsageToday).reduce((sum, v) => sum + (v || 0), 0);
    const perAccountAvg = activeAccounts.length > 0 ? totalUsed / activeAccounts.length : 0;
    if (perAccountAvg > 50) {
      score -= 20;
      issues.push({ severity: 'high', text: `Sending too many emails per account (avg ${Math.round(perAccountAvg)}/day)`, fix: 'Keep under 30 emails per account per day for new accounts' });
    } else if (perAccountAvg > 30) {
      score -= 10;
      issues.push({ severity: 'medium', text: `Email volume is high (avg ${Math.round(perAccountAvg)}/day)`, fix: 'Gradually increase volume after warmup period' });
    }

    // 3. Content Analysis
    if (compose.subject) {
      if (compose.subject.length < 20) {
        score -= 5;
        tips.push('Subject line is short - aim for 30-60 characters for best open rates');
      }
      if (compose.subject.length > 70) {
        score -= 5;
        tips.push('Subject line too long - may get truncated on mobile. Keep under 60 characters');
      }
      if (/^(re:|fw:|fwd:)/i.test(compose.subject) && !compose.inReplyTo) {
        score -= 15;
        issues.push({ severity: 'high', text: 'Fake RE:/FW: prefix detected - major spam trigger', fix: 'Remove RE:/FW: unless replying to actual email thread' });
      }
      if (compose.subject === compose.subject.toUpperCase() && compose.subject.length > 5) {
        score -= 10;
        issues.push({ severity: 'medium', text: 'Subject is ALL CAPS - spam filters flag this', fix: 'Use normal capitalization' });
      }
    } else {
      score -= 10;
      issues.push({ severity: 'medium', text: 'No subject line set', fix: 'Add a clear, professional subject line' });
    }

    // 4. Spam Score Integration
    if (spamScore > 60) {
      score -= 25;
      issues.push({ severity: 'critical', text: `High spam score (${spamScore}%) - email will likely go to spam`, fix: 'Use the AI rewrite feature to remove spam trigger words' });
    } else if (spamScore > 30) {
      score -= 10;
      issues.push({ severity: 'medium', text: `Moderate spam score (${spamScore}%)`, fix: 'Review and replace flagged words' });
    }

    // 5. Recipient Check
    const recipientCount = compose.recipients?.length || 0;
    if (recipientCount > 100) {
      score -= 15;
      issues.push({ severity: 'high', text: `Sending to ${recipientCount} recipients at once`, fix: 'Send in batches of 30-50 with delays between batches' });
    } else if (recipientCount > 50) {
      score -= 5;
      tips.push('Consider sending in smaller batches for better deliverability');
    }

    // 6. Body Content Check
    if (compose.body) {
      const bodyText = compose.body.replace(/<[^>]+>/g, '');
      const imgCount = (compose.body.match(/<img/gi) || []).length;
      const linkCount = (compose.body.match(/href=/gi) || []).length;

      if (imgCount > 3) {
        score -= 10;
        issues.push({ severity: 'medium', text: `Too many images (${imgCount})`, fix: 'Use max 1-2 images. Spam filters flag image-heavy emails' });
      }
      if (linkCount > 5) {
        score -= 10;
        issues.push({ severity: 'medium', text: `Too many links (${linkCount})`, fix: 'Limit to 2-3 relevant links maximum' });
      }
      if (bodyText.length < 50) {
        score -= 10;
        issues.push({ severity: 'medium', text: 'Email body is too short', fix: 'Write at least 3-4 sentences of personalized content' });
      }
      if (!bodyText.match(/\b(name|company|fleet|carrier|dot|mc)\b/i)) {
        score -= 5;
        tips.push('Personalize emails with carrier name, DOT#, or company details for better engagement');
      }
    }

    // 7. Best Practices Check
    if (!compose.accountId) {
      score -= 5;
      tips.push('Select a specific sender account instead of auto-select for consistent deliverability');
    }

    const finalScore = Math.max(score, 0);
    const rating = finalScore >= 80 ? 'excellent' : finalScore >= 60 ? 'good' : finalScore >= 40 ? 'needs-work' : 'poor';

    return { score: finalScore, rating, issues, tips };
  }, [accounts, compose, spamScore, accountUsageToday]);

  const copyTemplate = (template) => {
    navigator.clipboard.writeText(template).then(() => {
      setCopiedTemplate(template);
      setTimeout(() => setCopiedTemplate(null), 2000);
    });
  };

  const getScoreColor = (score) => {
    if (score >= 80) return '#10b981';
    if (score >= 60) return '#f59e0b';
    if (score >= 40) return '#f97316';
    return '#ef4444';
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical': return <ErrorIcon fontSize="small" sx={{ color: '#ef4444' }} />;
      case 'high': return <Warning fontSize="small" sx={{ color: '#f97316' }} />;
      case 'medium': return <Warning fontSize="small" sx={{ color: '#f59e0b' }} />;
      default: return <Lightbulb fontSize="small" sx={{ color: '#3b82f6' }} />;
    }
  };

  return (
    <Card
      variant="outlined"
      sx={{
        background: 'linear-gradient(135deg, rgba(16,185,129,0.04) 0%, rgba(59,130,246,0.04) 100%)',
        borderColor: getScoreColor(deliverabilityReport.score),
        borderWidth: 2,
      }}
    >
      <CardContent sx={{ p: isMobile ? 1.5 : 2 }}>
        {/* Header with Score */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={1}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: '12px',
                background: `linear-gradient(135deg, ${getScoreColor(deliverabilityReport.score)}20, ${getScoreColor(deliverabilityReport.score)}40)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Shield sx={{ color: getScoreColor(deliverabilityReport.score), fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="subtitle1" fontWeight={700}>
                Deliverability Score
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {deliverabilityReport.issues.length} issues • {deliverabilityReport.tips.length} tips
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <Box sx={{ width: isMobile ? 100 : 140 }}>
              <LinearProgress
                variant="determinate"
                value={deliverabilityReport.score}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: 'rgba(0,0,0,0.08)',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 4,
                    backgroundColor: getScoreColor(deliverabilityReport.score),
                  },
                }}
              />
            </Box>
            <Typography
              variant="h6"
              fontWeight={800}
              sx={{ color: getScoreColor(deliverabilityReport.score), minWidth: 45 }}
            >
              {deliverabilityReport.score}%
            </Typography>
            <IconButton size="small" onClick={() => setExpanded(!expanded)}>
              {expanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Stack>
        </Stack>

        {/* Quick Summary Alert */}
        {deliverabilityReport.issues.filter(i => i.severity === 'critical').length > 0 && (
          <Alert severity="error" sx={{ mt: 1.5 }} icon={<ErrorIcon />}>
            <strong>{deliverabilityReport.issues.filter(i => i.severity === 'critical').length} critical issue(s)</strong> detected - fix before sending
          </Alert>
        )}

        {/* Expanded Details */}
        <Collapse in={expanded}>
          <Box sx={{ mt: 2 }}>
            {/* Section Tabs */}
            <Stack direction="row" spacing={0.5} sx={{ mb: 2, flexWrap: 'wrap', gap: 0.5 }}>
              {[
                { id: 'overview', label: 'Issues', icon: <Assessment /> },
                { id: 'timing', label: 'Best Send Times', icon: <Schedule /> },
                { id: 'subjects', label: 'Subject Templates', icon: <Email /> },
                { id: 'spf', label: 'SPF/DKIM Guide', icon: <Security /> },
              ].map(tab => (
                <Chip
                  key={tab.id}
                  icon={tab.icon}
                  label={tab.label}
                  size="small"
                  onClick={() => setActiveSection(tab.id)}
                  sx={{
                    fontWeight: activeSection === tab.id ? 700 : 500,
                    backgroundColor: activeSection === tab.id ? 'primary.main' : 'transparent',
                    color: activeSection === tab.id ? 'white' : 'text.primary',
                    border: activeSection === tab.id ? 'none' : '1px solid rgba(0,0,0,0.12)',
                    '&:hover': { backgroundColor: activeSection === tab.id ? 'primary.dark' : 'rgba(0,0,0,0.04)' },
                  }}
                />
              ))}
            </Stack>

            <Divider sx={{ mb: 2 }} />

            {/* Overview - Issues & Tips */}
            {activeSection === 'overview' && (
              <Stack spacing={1.5}>
                {deliverabilityReport.issues.length === 0 && deliverabilityReport.tips.length === 0 && (
                  <Alert severity="success" icon={<CheckCircle />}>
                    Excellent! No deliverability issues detected. Your email is ready to send.
                  </Alert>
                )}
                {deliverabilityReport.issues.map((issue, idx) => (
                  <Alert
                    key={idx}
                    severity={issue.severity === 'critical' ? 'error' : issue.severity === 'high' ? 'warning' : 'info'}
                    icon={getSeverityIcon(issue.severity)}
                    sx={{ '& .MuiAlert-message': { width: '100%' } }}
                  >
                    <Typography variant="body2" fontWeight={600}>{issue.text}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Fix: {issue.fix}
                    </Typography>
                  </Alert>
                ))}
                {deliverabilityReport.tips.map((tip, idx) => (
                  <Alert key={`tip-${idx}`} severity="info" icon={<Lightbulb />}>
                    <Typography variant="body2">{tip}</Typography>
                  </Alert>
                ))}
              </Stack>
            )}

            {/* Best Send Times */}
            {activeSection === 'timing' && (
              <Stack spacing={1.5}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Optimal send times for B2B trucking/logistics emails (based on industry data):
                </Typography>
                {BEST_SEND_TIMES.map((slot, idx) => (
                  <Stack key={idx} direction="row" alignItems="center" spacing={2}>
                    <Typography variant="body2" fontWeight={600} sx={{ minWidth: 90 }}>
                      {slot.day}
                    </Typography>
                    <Box sx={{ flex: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={slot.score}
                        sx={{
                          height: 6,
                          borderRadius: 3,
                          backgroundColor: 'rgba(0,0,0,0.06)',
                          '& .MuiLinearProgress-bar': {
                            borderRadius: 3,
                            background: `linear-gradient(90deg, #667eea, #764ba2)`,
                          },
                        }}
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 160, textAlign: 'right' }}>
                      {slot.time}
                    </Typography>
                    <Chip label={`${slot.score}%`} size="small" color={slot.score >= 90 ? 'success' : 'default'} />
                  </Stack>
                ))}
                <Alert severity="info" sx={{ mt: 1 }}>
                  <strong>Pro Tip:</strong> Truck owners and dispatchers check email early morning before starting routes. 
                  Avoid weekends - response rates drop 60%.
                </Alert>
              </Stack>
            )}

            {/* Subject Line Templates */}
            {activeSection === 'subjects' && (
              <Stack spacing={1}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Proven subject lines for trucking industry outreach (spam-filter safe):
                </Typography>
                {SUBJECT_TEMPLATES.map((item, idx) => (
                  <Stack
                    key={idx}
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{
                      p: 1,
                      borderRadius: 1,
                      border: '1px solid rgba(0,0,0,0.08)',
                      '&:hover': { backgroundColor: 'rgba(102,126,234,0.05)' },
                    }}
                  >
                    <Chip label={item.category} size="small" variant="outlined" sx={{ minWidth: 90 }} />
                    <Typography variant="body2" sx={{ flex: 1, fontFamily: 'monospace', fontSize: '0.85rem' }}>
                      {item.template}
                    </Typography>
                    <Tooltip title={copiedTemplate === item.template ? 'Copied!' : 'Copy'}>
                      <IconButton size="small" onClick={() => copyTemplate(item.template)}>
                        <ContentCopy fontSize="small" color={copiedTemplate === item.template ? 'success' : 'action'} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                ))}
                <Alert severity="info" sx={{ mt: 1 }}>
                  <strong>Replace placeholders</strong> like {'{{company}}'}, {'{{state}}'}, {'{{dot}}'} with actual carrier data from search results for best personalization.
                </Alert>
              </Stack>
            )}

            {/* SPF/DKIM Setup Guide */}
            {activeSection === 'spf' && (
              <Stack spacing={2}>
                <Typography variant="body2" color="text.secondary">
                  Critical DNS records to ensure your emails reach the inbox:
                </Typography>

                {/* SPF Record */}
                <Card variant="outlined" sx={{ backgroundColor: 'rgba(16,185,129,0.04)' }}>
                  <CardContent sx={{ p: 1.5 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                      <Dns fontSize="small" color="success" />
                      <Typography variant="subtitle2" fontWeight={700}>SPF Record (TXT)</Typography>
                      <Chip label="Required" size="small" color="error" />
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      Add this TXT record to your domain DNS:
                    </Typography>
                    <Box
                      sx={{
                        p: 1,
                        backgroundColor: '#1e293b',
                        borderRadius: 1,
                        fontFamily: 'monospace',
                        fontSize: '0.8rem',
                        color: '#10b981',
                        overflowX: 'auto',
                      }}
                    >
                      v=spf1 include:_spf.google.com include:spf.hostinger.com ~all
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      Modify based on your email provider. For Hostinger, the above works. For custom SMTP, add your server IP.
                    </Typography>
                  </CardContent>
                </Card>

                {/* DKIM */}
                <Card variant="outlined" sx={{ backgroundColor: 'rgba(59,130,246,0.04)' }}>
                  <CardContent sx={{ p: 1.5 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                      <VerifiedUser fontSize="small" color="primary" />
                      <Typography variant="subtitle2" fontWeight={700}>DKIM Record</Typography>
                      <Chip label="Required" size="small" color="error" />
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      DKIM digitally signs emails to prove they came from your domain. Setup in Hostinger:
                    </Typography>
                    <List dense sx={{ py: 0 }}>
                      <ListItem sx={{ py: 0.25 }}>
                        <ListItemIcon sx={{ minWidth: 28 }}>
                          <CheckCircle fontSize="small" color="primary" />
                        </ListItemIcon>
                        <ListItemText primary="Go to Hostinger → Emails → Your Domain" primaryTypographyProps={{ variant: 'caption' }} />
                      </ListItem>
                      <ListItem sx={{ py: 0.25 }}>
                        <ListItemIcon sx={{ minWidth: 28 }}>
                          <CheckCircle fontSize="small" color="primary" />
                        </ListItemIcon>
                        <ListItemText primary="Navigate to Email Authentication → Enable DKIM" primaryTypographyProps={{ variant: 'caption' }} />
                      </ListItem>
                      <ListItem sx={{ py: 0.25 }}>
                        <ListItemIcon sx={{ minWidth: 28 }}>
                          <CheckCircle fontSize="small" color="primary" />
                        </ListItemIcon>
                        <ListItemText primary="Copy the DKIM record and add as TXT record in DNS Zone" primaryTypographyProps={{ variant: 'caption' }} />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>

                {/* DMARC */}
                <Card variant="outlined" sx={{ backgroundColor: 'rgba(139,92,246,0.04)' }}>
                  <CardContent sx={{ p: 1.5 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                      <Security fontSize="small" sx={{ color: '#8b5cf6' }} />
                      <Typography variant="subtitle2" fontWeight={700}>DMARC Record</Typography>
                      <Chip label="Recommended" size="small" color="warning" />
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      Add DMARC to protect your domain and improve deliverability:
                    </Typography>
                    <Box
                      sx={{
                        p: 1,
                        backgroundColor: '#1e293b',
                        borderRadius: 1,
                        fontFamily: 'monospace',
                        fontSize: '0.8rem',
                        color: '#a78bfa',
                        overflowX: 'auto',
                      }}
                    >
                      v=DMARC1; p=quarantine; rua=mailto:dmarc@carrierfinderpro.com; pct=100
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      Host: _dmarc.yourdomain.com • Type: TXT
                    </Typography>
                  </CardContent>
                </Card>

                {/* Custom Tracking Domain */}
                <Card variant="outlined" sx={{ backgroundColor: 'rgba(245,158,11,0.04)' }}>
                  <CardContent sx={{ p: 1.5 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                      <Speed fontSize="small" sx={{ color: '#f59e0b' }} />
                      <Typography variant="subtitle2" fontWeight={700}>Custom Tracking Domain</Typography>
                      <Chip label="Advanced" size="small" />
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      If using open/click tracking, set up a custom tracking domain (e.g., track.yourdomain.com) 
                      instead of default tracking URLs. This prevents spam filters from flagging shared tracking domains.
                    </Typography>
                  </CardContent>
                </Card>

                <Alert severity="warning">
                  <strong>Important:</strong> DNS changes take 24-48 hours to propagate. Verify records using 
                  <Button
                    size="small"
                    sx={{ ml: 0.5, textTransform: 'none', fontWeight: 600 }}
                    onClick={() => window.open('https://mxtoolbox.com/spf.aspx', '_blank')}
                  >
                    MXToolbox
                  </Button>
                </Alert>
              </Stack>
            )}
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default EmailDeliverabilityGuide;
